/**
 * PRP-WEB-1 W3.3: the room panel (side panel; a bottom sheet under 640 px). Name; Length and
 * Width for a rectangle, each wall's length otherwise; ceiling height; openings (kind, width,
 * height, position along the wall; add and delete). Every change is a `@sketch-core` action
 * handed to the page, which runs it through History (Undo/Redo) and redraws.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { X, Trash2 } from 'lucide-react';
import {
  addOpening, area, deleteOpening, edgeOf, lengthInfo, renameRoom, setCeiling, updateOpening, dist,
  type Opening, type Plan, type Room,
} from '@sketch-core';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { FeetInchesField } from './FeetInchesField';
import { formatFeetInches } from './feetInches';
import { rectSides, type EditResult, type RoomEditability } from './planEdit';

type Action = (p: Plan) => { ok: boolean; message?: string };

export interface RoomPanelProps {
  plan: Plan;
  room: Room;
  editability: RoomEditability;
  /** Run a core action; returns the outcome (a refused action carries its message). */
  onAction: (action: Action) => EditResult;
  /** Set a wall's length (the phone's Wall length sheet). */
  onWallLength: (vid: number, ft: number, opts?: { moveEnd?: boolean; chosen?: boolean }) => EditResult;
  /** Highlight a wall on the plan while its field is focused (null = none). */
  onHighlightWall: (vid: number | null) => void;
  onClose: () => void;
  /** PRP-WEB-2: more sections under the openings (the subroom list). */
  extra?: ReactNode;
}

const KIND_LABEL: Record<Opening['kind'], string> = { door: 'Door', window: 'Window', missing: 'Opening' };

export function RoomPanel({ plan, room, editability, onAction, onWallLength, onHighlightWall, onClose, extra }: RoomPanelProps) {
  const editable = editability.editable;
  const [msg, setMsg] = useState<{ tone: 'bad' | 'ok'; text: string; options?: { label: string; moveQ: boolean }[]; retry?: (moveQ: boolean) => void } | null>(null);
  const [name, setName] = useState(room.name);
  const [newKind, setNewKind] = useState<Opening['kind']>('door');
  const [newWall, setNewWall] = useState<number>(room.corners[0]?.id ?? 0);
  useEffect(() => { setName(room.name); }, [room.name, room.id]);
  useEffect(() => { setMsg(null); setNewWall(room.corners[0]?.id ?? 0); }, [room.id]);

  const walls = room.corners.map((c, i) => ({ vid: c.id, label: `Wall ${i + 1}` }));
  const wallName = (vid: number) => walls.find((w) => w.vid === vid)?.label ?? 'Wall';
  const sides = rectSides(room);

  /**
   * A field's outcome: a refusal goes back to the field (shown under it); a note, or a choice
   * of walls to move, shows under the Size block.
   */
  const show = (res: EditResult, retry?: (moveQ: boolean) => void): string | void => {
    if (res.ok) { setMsg(res.note ? { tone: 'ok', text: res.note } : null); return undefined; }
    if (res.options?.length) { setMsg({ tone: 'bad', text: res.message, options: res.options, retry }); return undefined; }
    setMsg(null);
    return res.message;
  };
  const field = (a: Action) => show(onAction(a));
  /** A button's outcome (add, delete): a refusal shows under the Size block. */
  const act = (a: Action) => {
    const res = onAction(a);
    setMsg(res.ok ? null : { tone: 'bad', text: res.message });
  };
  const setLen = (vid: number) => (ft: number) =>
    show(onWallLength(vid, ft), (moveQ) => show(onWallLength(vid, ft, { moveEnd: moveQ, chosen: true })));

  const wallLen = (vid: number) => {
    const info = lengthInfo(plan, room.id, vid);
    if ('ok' in info) {
      const E = edgeOf(room, vid);
      return { ft: E ? dist(E.a, E.b) : 0, straight: false };
    }
    return { ft: info.currentFt, straight: true };
  };

  const focusWall = (vid: number) => (f: boolean) => onHighlightWall(f ? vid : null);

  return (
    <aside
      data-testid="room-panel"
      aria-label={`Room ${room.name}`}
      className="flex min-h-0 flex-col overflow-y-auto bg-[var(--bg-2)] text-[var(--tx-hi)]
        max-sm:z-20 max-sm:max-h-[55%] max-sm:shrink-0 max-sm:rounded-t-[var(--r-xl)] max-sm:border-t max-sm:border-[var(--bd)] max-sm:shadow-[0_-8px_24px_rgba(0,0,0,.25)]
        sm:w-[340px] sm:shrink-0 sm:border-l sm:border-[var(--bd)]"
    >
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-[var(--bd-subtle)] bg-[var(--bg-2)] px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-[620]" data-testid="room-panel-title">{room.name}</div>
          <div className="text-[11.5px] text-[var(--tx-faint)]">{Math.round(Math.abs(area(room)))} SF · ceiling {formatFeetInches(room.ceilingFt)}</div>
        </div>
        <Button variant="icon" size="sm" aria-label="Close room panel" onClick={onClose}><X /></Button>
      </div>

      <div className="space-y-5 px-4 py-4">
        {!editable && (
          <p data-testid="room-readonly" className="rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-3)] p-3 text-[12.5px] text-[var(--tx-mid)]">
            {editability.reason}
          </p>
        )}

        <section className="space-y-1">
          <label htmlFor="room-name" className="mb-1 block text-[11.5px] font-[560] text-[var(--tx-lo)]">Name</label>
          <Input id="room-name" data-testid="room-name" value={name} disabled={!editable}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => { if (name.trim() !== room.name) act((p) => renameRoom(p, room.id, name)); }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setName(room.name); }} />
        </section>

        <section>
          <h3 className="mb-2 text-[12px] font-[620] uppercase tracking-[.04em] text-[var(--tx-lo)]">Size</h3>
          {sides ? (
            <div className="grid grid-cols-2 gap-3">
              <FeetInchesField label="Length" testId="room-length" valueFt={wallLen(sides.lengthVid).ft} disabled={!editable}
                onCommit={setLen(sides.lengthVid)} onFocusChange={focusWall(sides.lengthVid)} />
              <FeetInchesField label="Width" testId="room-width" valueFt={wallLen(sides.widthVid).ft} disabled={!editable}
                onCommit={setLen(sides.widthVid)} onFocusChange={focusWall(sides.widthVid)} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {walls.map((w) => {
                const l = wallLen(w.vid);
                return l.straight ? (
                  <FeetInchesField key={w.vid} label={w.label} testId={`room-wall-${w.vid}`} valueFt={l.ft} disabled={!editable}
                    onCommit={setLen(w.vid)} onFocusChange={focusWall(w.vid)} />
                ) : (
                  <div key={w.vid} className="text-[12px] text-[var(--tx-lo)]">
                    <div className="mb-1 text-[11.5px] font-[560]">{w.label}</div>
                    <div className="h-[34px] leading-[34px] text-[var(--tx-mid)]" title="Angled walls are set on the phone">{formatFeetInches(l.ft)} (angled)</div>
                  </div>
                );
              })}
            </div>
          )}
          {msg && (
            <div role={msg.tone === 'bad' ? 'alert' : 'status'} data-testid="room-panel-message"
              className={`mt-2 text-[12px] ${msg.tone === 'bad' ? 'text-[var(--bad)]' : 'text-[var(--ok)]'}`}>
              {msg.text}
              {msg.options && msg.retry && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {msg.options.map((o) => (
                    <Button key={o.label} size="sm" onClick={() => msg.retry!(o.moveQ)}>{o.label}</Button>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <section>
          <FeetInchesField label="Ceiling height" testId="room-ceiling" valueFt={room.ceilingFt} disabled={!editable}
            onCommit={(ft) => field((p) => setCeiling(p, room.id, ft))} />
        </section>

        <section>
          <h3 className="mb-2 text-[12px] font-[620] uppercase tracking-[.04em] text-[var(--tx-lo)]">
            Openings <span className="font-normal normal-case tracking-normal text-[var(--tx-faint)]">({room.openings.length})</span>
          </h3>
          {room.openings.length === 0 && <p className="text-[12px] text-[var(--tx-faint)]">No doors, windows or openings.</p>}
          <ul className="space-y-3">
            {room.openings.map((o) => (
              <li key={o.id} data-testid="room-opening" data-opening={o.id}
                className="rounded-[var(--r-md)] border border-[var(--bd-subtle)] bg-[var(--bg-1)] p-3">
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-[12.5px] font-[600]">{KIND_LABEL[o.kind]}</span>
                  <span className="text-[11.5px] text-[var(--tx-faint)]">on {wallName(o.edgeStart)}</span>
                  {editable && (
                    <Button variant="ghost" size="sm" className="ml-auto" aria-label={`Delete ${KIND_LABEL[o.kind].toLowerCase()} on ${wallName(o.edgeStart)}`}
                      onClick={() => act((p) => deleteOpening(p, room.id, o.id))}><Trash2 /></Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <FeetInchesField label="Width" valueFt={o.widthFt} disabled={!editable}
                    onCommit={(ft) => field((p) => updateOpening(p, room.id, o.id, { widthFt: ft }))} onFocusChange={focusWall(o.edgeStart)} />
                  {(o.kind === 'window' || o.heightFt != null) ? (
                    <FeetInchesField label="Height" valueFt={o.heightFt ?? 0} disabled={!editable}
                      onCommit={(ft) => field((p) => updateOpening(p, room.id, o.id, { heightFt: ft }))} onFocusChange={focusWall(o.edgeStart)} />
                  ) : <div />}
                  <FeetInchesField label="From corner" valueFt={o.offFt} disabled={!editable}
                    onCommit={(ft) => field((p) => updateOpening(p, room.id, o.id, { offFt: ft }))} onFocusChange={focusWall(o.edgeStart)} />
                </div>
              </li>
            ))}
          </ul>
          {editable && (
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <label className="text-[11.5px] text-[var(--tx-lo)]">
                <span className="mb-1 block font-[560]">Add</span>
                <select data-testid="opening-kind" value={newKind} onChange={(e) => setNewKind(e.target.value as Opening['kind'])}
                  className="h-[34px] rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-inset)] px-2 text-[13px] text-[var(--tx-hi)]">
                  <option value="door">Door</option>
                  <option value="window">Window</option>
                  <option value="missing">Opening</option>
                </select>
              </label>
              <label className="text-[11.5px] text-[var(--tx-lo)]">
                <span className="mb-1 block font-[560]">on</span>
                <select data-testid="opening-wall" value={newWall} onChange={(e) => setNewWall(Number(e.target.value))}
                  onFocus={() => onHighlightWall(newWall)} onBlur={() => onHighlightWall(null)}
                  className="h-[34px] rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-inset)] px-2 text-[13px] text-[var(--tx-hi)]">
                  {walls.map((w) => <option key={w.vid} value={w.vid}>{w.label} ({formatFeetInches(wallLen(w.vid).ft)})</option>)}
                </select>
              </label>
              <Button size="md" data-testid="opening-add" onClick={() => {
                const L = wallLen(newWall).ft;
                act((p) => addOpening(p, room.id, newWall, L / 2, newKind));
              }}>Add</Button>
            </div>
          )}
        </section>
        {extra}
      </div>
    </aside>
  );
}

export default RoomPanel;
